import { getContext, setContext } from 'svelte';
import type { ToastData } from './types';

// Because context is a lifecycle method, we need to decouple retrieval and creation from usage. Usage is from ./toast.ts 
export class ToastState {
    // The list of toasts to display
    toasts = $state<ToastData[]>([]);
    // Keep track of timeouts so they can be cleared if the toast is removed manually
    toastToTimeoutMap = new Map<string, NodeJS.Timeout>(); // NodeJS.Timeout is the type returned by setTimeout in Node.js. It results in a number

    constructor() {}

    update(toasts: ToastData[]) {
        this.toasts = toasts;
    }

    addToast({title, description, durationMs = 5000, type = 'info'}: {title: string, description: string, durationMs?: number, type?: 'info' | 'success' | 'warning' | 'error'}) {
        // Not v4 dependant
        const id = crypto.randomUUID();
        const newToast: ToastData = {
            id,
            title,
            description,
            type
        }

        this.toasts.push(newToast);
        this.toastToTimeoutMap.set(
            id, 
            setTimeout(() => {
                this.removeToast(id);
            }, durationMs)
        );
    }

    removeToast(id: string) {
        const timeout = this.toastToTimeoutMap.get(id)
        if(timeout) {
            clearTimeout(timeout);
            this.toastToTimeoutMap.delete(id);
        }
        this.toasts = this.toasts.filter(toast => toast.id !== id);
    }

    // Should be called from a component's onDestroy 
    destroy() {
        // Cleanup timeouts on destroy
        for(const timeout of this.toastToTimeoutMap.values()) {
            clearTimeout(timeout);
        }
        this.toastToTimeoutMap.clear();
    }

}

// Unique key for the toast state context
const TOAST_KEY = Symbol('TOAST')
// Initializes an instance of toastState and sets it in context
export const setToastState = () =>  setContext(TOAST_KEY, new ToastState());
// Returntype used to only declare type once
export const getToastState = () =>  getContext<ReturnType<typeof setToastState>>(TOAST_KEY);